import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import { memo, useEffect, useMemo, useState } from "react";
import type { GeoDirectionalLine, GeoScatterBoundary, GeoScatterPoint } from "@/shared/charts";
import type {
  LiveTrackPoint,
  LiveTrailSegment,
  ObservationAreaBoundary,
  SensorMarkerPoint,
} from "@/features/deployment-dashboard/types";
import {
  createFacilitySchematicChartConfig,
  createFacilitySchematicMarkers,
  createObservationAreaBoundaries,
} from "@/features/deployment-dashboard/widgets/panels/facility-schematic/facility-schematic-option";
import { useFacilitySchematicAssets } from "@/features/deployment-dashboard/widgets/panels/facility-schematic/hooks/use-facility-schematic-assets";
import { registerFacilitySchematicMap } from "@/features/deployment-dashboard/widgets/panels/facility-schematic/register-facility-schematic-map";
import {
  floorplanPixelToWorld,
  type WorldToPixelTransformAsset,
} from "@/features/deployment-dashboard/widgets/panels/facility-schematic/services/world-to-pixel-transform";
import { useLiveHeatmapLayer } from "@/features/deployment-dashboard/live-view/hooks/use-live-heatmap-layer";
import { LIVE_HEATMAP_CONFIG } from "@/features/deployment-dashboard/live-view/config/live-heatmap-config";
import { GeoScatterChart } from "@/shared/charts";
import { Callout, LoadingState } from "@/shared/ui";

const GENERIC_LOAD_ERROR_MESSAGE =
  "Failed to load the facility schematic image and transform. Try reloading the page.";
const EMPTY_LIVE_TRACKS: LiveTrackPoint[] = [];

interface FacilitySchematicProps {
  deploymentId: string;
  /** Deployment area whose map/floor plan is displayed; no floor plan is shown until known. */
  areaId?: string;
  /** Optional local floor plan used by demos while preserving the shared ECharts map renderer. */
  floorplanOverride?: { imageUrl: string; imageSize: { width: number; height: number } };
  /** Live-tracked object dots, already mapped into the schematic's pixel coordinate space. */
  liveTracks?: LiveTrackPoint[];
  trails?: LiveTrailSegment[];
  /** Source inputs for the facility-owned heatmap renderer. */
  heatmapTracks?: LiveTrackPoint[];
  heatmapTransform?: Pick<WorldToPixelTransformAsset, "matrix">;
  heatmapMapSize?: { width: number; height: number };
  showHeatmap?: boolean;
  /** Runtime sensor markers, already mapped into the schematic's pixel coordinate space. */
  sensorPoints?: SensorMarkerPoint[];
  showSensors?: boolean;
  /** Observation area boundaries to highlight, already mapped into the schematic's pixel coordinate space. */
  observationAreas?: ObservationAreaBoundary[];
  /** Whether observation-area hover tooltips are interactive. */
  observationAreaTooltipsEnabled?: boolean;
  /** AOI polygons already mapped into floor-plan pixel space. */
  aoiBoundaries?: GeoScatterBoundary[];
  /** Queue area polygons already mapped into floor-plan pixel space. */
  queueBoundaries?: GeoScatterBoundary[];
  /** Queue entrance and exit lines already mapped into floor-plan pixel space. */
  directionalLines?: GeoDirectionalLine[];
  /** AOI draft vertices already mapped into floor-plan pixel space. */
  aoiDraftPoints?: GeoScatterPoint[];
  /** Receives a clicked map position converted into the area's world coordinate frame. */
  onMapPointClick?: (point: { x: number; y: number }) => void;
  /** Receives a dragged AOI vertex converted back into the area's world coordinate frame. */
  onMoveAoiDraftPoint?: (pointId: string, point: { x: number; y: number }) => void;
  /** Allows dragging the complete draft polygon; intended only for an existing AOI in edit mode. */
  canMoveAoiDraftShape?: boolean;
  /** Invoked with an AOI draft vertex's id while hovered (not dragging), or null once the cursor leaves it. */
  onHoverAoiDraftPoint?: (pointId: string | null) => void;
  /** Clickable "+" markers at an AOI draft edge's midpoint, already mapped into floor-plan pixel space. */
  aoiInsertionPoints?: GeoScatterPoint[];
  /** Invoked with an AOI insertion point's id when it's clicked, to insert a new vertex at that edge's midpoint. */
  onInsertAoiDraftPoint?: (pointId: string) => void;
  /** Receives a double-clicked map position (converted into world coordinates) for selecting an AOI by its boundary. */
  onAoiBoundaryDoubleClick?: (point: { x: number; y: number }) => void;
  /** Bump this to re-focus the map on `observationAreas`, e.g. from a "Focus Selected Area(s)" control. */
  focusRequestId?: number;
  /** Notified whenever the map becomes focused on `observationAreas` (`true`) or the user manually pans/zooms away from that focus (`false`). */
  onFocusedChange?: (focused: boolean) => void;
  /** Optional callback to reset/refocus the view on observation areas. */
  onResetFocus?: () => void;
}

/**
 * Operational facility schematic rendered as a geo map from the deployment's
 * floor plan image (fetched and cache-first via `useFacilitySchematicAssets`,
 * then registered as an ECharts map once loaded), with a scatter overlay of
 * selectable, severity-colored notification markers, and a live occupancy
 * heatmap. Marker click selection stays plain data
 * (selectedNotificationId drives the highlighted marker's border/glow), not
 * internal chart state.
 */
export const FacilitySchematic = memo(function FacilitySchematic({
  deploymentId,
  areaId,
  floorplanOverride,
  liveTracks = [],
  trails = [],
  heatmapTracks = EMPTY_LIVE_TRACKS,
  heatmapTransform,
  heatmapMapSize,
  showHeatmap = true,
  sensorPoints = [],
  showSensors = false,
  observationAreas = [],
  observationAreaTooltipsEnabled = true,
  aoiBoundaries = [],
  queueBoundaries = [],
  directionalLines = [],
  aoiDraftPoints = [],
  onMapPointClick,
  onMoveAoiDraftPoint,
  canMoveAoiDraftShape = false,
  onHoverAoiDraftPoint,
  aoiInsertionPoints = [],
  onInsertAoiDraftPoint,
  onAoiBoundaryDoubleClick,
  focusRequestId,
  onFocusedChange,
  onResetFocus,
}: FacilitySchematicProps) {
  const theme = useTheme();
  const chartConfig = useMemo(() => createFacilitySchematicChartConfig(), []);
  const [registeredMapSource, setRegisteredMapSource] = useState<string | null>(null);
  const { status, floorplanImageUrl, floorplanImageSize, transform } = useFacilitySchematicAssets(
    deploymentId,
    areaId,
    floorplanOverride == null
  );
  const resolveWorldCoordinate = useMemo(() => {
    if (!transform) return undefined;

    return ([pixelX, pixelY]: [number, number]) => {
      const world = floorplanPixelToWorld(transform, pixelX, pixelY);
      return { key: "utm", x: world.x, y: world.y };
    };
  }, [transform]);
  const heatmapLayer = useLiveHeatmapLayer(
    heatmapTransform,
    heatmapTracks,
    heatmapMapSize,
    LIVE_HEATMAP_CONFIG,
    showHeatmap
  );
  const displayedFloorplanImageUrl = floorplanOverride?.imageUrl ?? floorplanImageUrl;
  const displayedFloorplanImageSize = floorplanOverride?.imageSize ?? floorplanImageSize;
  const mapSource =
    displayedFloorplanImageUrl && displayedFloorplanImageSize
      ? JSON.stringify([
          displayedFloorplanImageUrl,
          displayedFloorplanImageSize.width,
          displayedFloorplanImageSize.height,
        ])
      : null;

  useEffect(() => {
    if (!displayedFloorplanImageUrl || !displayedFloorplanImageSize || !mapSource) return;

    registerFacilitySchematicMap(displayedFloorplanImageUrl, displayedFloorplanImageSize);
    setRegisteredMapSource(mapSource);
  }, [displayedFloorplanImageUrl, displayedFloorplanImageSize, mapSource]);

  const { points, lineSegments } = useMemo(
    () =>
      createFacilitySchematicMarkers(
        liveTracks,
        showSensors ? sensorPoints : [],
        theme,
        trails,
        observationAreas,
        displayedFloorplanImageSize?.height ?? 0
      ),
    [
      liveTracks,
      showSensors,
      sensorPoints,
      theme,
      trails,
      observationAreas,
      displayedFloorplanImageSize,
    ]
  );
  const observationAreaBoundaries = useMemo(
    () => createObservationAreaBoundaries(observationAreas, observationAreaTooltipsEnabled),
    [observationAreas, observationAreaTooltipsEnabled]
  );
  const handleMapClick = useMemo(() => {
    if (!transform || !onMapPointClick) return undefined;

    return ([pixelX, pixelY]: [number, number]) => {
      onMapPointClick(floorplanPixelToWorld(transform, pixelX, pixelY));
    };
  }, [transform, onMapPointClick]);
  const handleMoveAoiPoint = useMemo(() => {
    if (!transform || !onMoveAoiDraftPoint) return undefined;

    return (pointId: string, [pixelX, pixelY]: [number, number]) => {
      onMoveAoiDraftPoint(pointId, floorplanPixelToWorld(transform, pixelX, pixelY));
    };
  }, [transform, onMoveAoiDraftPoint]);
  const handleAoiBoundaryDoubleClick = useMemo(() => {
    if (!transform || !onAoiBoundaryDoubleClick) return undefined;

    return ([pixelX, pixelY]: [number, number]) => {
      onAoiBoundaryDoubleClick(floorplanPixelToWorld(transform, pixelX, pixelY));
    };
  }, [transform, onAoiBoundaryDoubleClick]);

  if (!floorplanOverride && (status === "map-error" || status === "image-error")) {
    return (
      <Callout severity="error" title="Facility schematic unavailable">
        {GENERIC_LOAD_ERROR_MESSAGE}
      </Callout>
    );
  }

  if (!floorplanOverride && status === "no-floorplan") {
    return (
      <Callout severity="info" title="No floor plan configured">
        No floor plan configured for this area.
      </Callout>
    );
  }

  if (
    (!floorplanOverride && (status === "loading-map" || status === "loading-image")) ||
    !displayedFloorplanImageUrl ||
    !displayedFloorplanImageSize ||
    registeredMapSource !== mapSource
  ) {
    return <LoadingState message="Loading facility schematic..." />;
  }

  return (
    // Cursor is intentionally left unset here: `GeoScatterChart` owns cursor
    // state dynamically (idle crosshair/default, node grab/grabbing, shape
    // move, insertion-point pointer, ...) via zrender's `setCursorStyle`,
    // which sets `cursor` on its canvas's wrapper div. A `cursor` rule
    // targeting `canvas` here would take precedence over that inherited
    // value (an element's own explicit `cursor` always beats an inherited
    // one, regardless of selector specificity) and mask every dynamic update.
    <Box sx={{ width: "100%", height: "100%" }}>
      <GeoScatterChart
        key={`${deploymentId}:${mapSource}`}
        markers={[]}
        points={[...points, ...aoiDraftPoints]}
        lineSegments={lineSegments}
        directionalLines={directionalLines}
        heatmapLayer={showHeatmap ? heatmapLayer : null}
        boundaries={[...observationAreaBoundaries, ...aoiBoundaries, ...queueBoundaries]}
        focusBoundaries={observationAreaBoundaries}
        config={chartConfig}
        onMapClick={handleMapClick}
        editablePoints={handleMoveAoiPoint ? aoiDraftPoints : []}
        onMovePoint={handleMoveAoiPoint}
        canMoveEditableShape={canMoveAoiDraftShape}
        onHoverPoint={onHoverAoiDraftPoint}
        onMapDoubleClick={handleAoiBoundaryDoubleClick}
        insertionPoints={aoiInsertionPoints}
        onInsertPoint={onInsertAoiDraftPoint}
        focusRequestId={focusRequestId}
        onFocusedChange={onFocusedChange}
        onResetFocus={onResetFocus}
        resolveSecondaryCoordinate={resolveWorldCoordinate}
      />
    </Box>
  );
});
